import { useState, useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { trackFunnelEvent } from '../../services/analytics/funnelTracker';

// ─── Color tokens ───

const C = {
  bg: '#FFFFFF',
  bgSoft: '#F8FAFC',
  bgCard: '#FFFFFF',
  bgDark: '#0F0F14',
  bgDarkCard: 'rgba(255,255,255,0.035)',
  bgDarkBorder: 'rgba(255,255,255,0.07)',
  btn: '#18181B',
  btnHover: '#27272A',
  btnGlow: 'rgba(24,24,27,.12)',
  accent: '#E11D48',
  accentSoft: '#FFF1F2',
  amber: '#B45309',
  amberSoft: '#FFFBEB',
  navy: '#0F172A',
  text: '#18181B',
  textSec: '#52525B',
  textMuted: '#A1A1AA',
  border: '#E4E4E7',
  borderLight: '#F4F4F5',
  glass: 'rgba(255,255,255,0.75)',
};

// ─── FadeIn on scroll ───

function FadeIn({
  children,
  delay = 0,
  direction = 'up',
  style: extra = {},
}: {
  children: ReactNode;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  style?: CSSProperties;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setV(true);
      },
      { threshold: 0.12 },
    );
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  const t: Record<string, string> = {
    up: 'translateY(24px)',
    down: 'translateY(-24px)',
    left: 'translateX(24px)',
    right: 'translateX(-24px)',
    none: 'none',
  };
  return (
    <div
      ref={ref}
      style={{
        opacity: v ? 1 : 0,
        transform: v ? 'none' : t[direction],
        transition: `opacity .6s cubic-bezier(.16,1,.3,1) ${delay}s, transform .6s cubic-bezier(.16,1,.3,1) ${delay}s`,
        ...extra,
      }}
    >
      {children}
    </div>
  );
}

// ─── Data ───

const biomarkers = [
  { name: 'CBC', std: true },
  { name: 'Basic Metabolic Panel', std: true },
  { name: 'TSH', std: true },
  { name: 'Free T3 / Free T4', std: false },
  { name: 'Thyroid Antibodies', std: false },
  { name: 'Estradiol (Cycle-Mapped)', std: false },
  { name: 'Progesterone (Luteal)', std: false },
  { name: 'DHEA-S', std: false },
  { name: 'Fasting Insulin', std: false },
  { name: 'hs-CRP', std: false },
  { name: 'Homocysteine', std: false },
  { name: 'Vitamin D (25-OH)', std: false },
  { name: 'Ferritin', std: false },
  { name: 'Cortisol (AM Draw)', std: false },
];

const steps = [
  { n: '01', title: 'Upload Any Lab', desc: "Quest, Labcorp, your doctor's office — drop in any recent bloodwork. The AI reads 40+ biomarkers in seconds.", icon: '↑' },
  { n: '02', title: 'See the Full Picture', desc: "Know where you fall across functional, optimal, and performance ranges — not just the pass/fail 'in range' system.", icon: '◎' },
  { n: '03', title: 'Understand Every Number', desc: 'The AI walks you through every result in plain language — what it means, why it matters, what the research says.', icon: '✦' },
  { n: '04', title: 'Decide Your Next Move', desc: 'Take your report to your doctor, explore targeted supplements, or connect with a licensed physician. Your call.', icon: '→' },
];

const testimonials = [
  {
    quote: "I uploaded my LabCorp results from my annual physical. Eleven minutes later I understood why my hair was thinning and my energy crashed every afternoon. My doctor had marked everything 'normal.'",
    name: 'Rachel Moreno',
    detail: '34, Austin TX',
  },
  {
    quote: "I was honestly skeptical — another AI health thing, great. But it caught that my ferritin was technically 'in range' at 18 while optimal starts at 50. My doctor confirmed it and started iron therapy that week.",
    name: 'Marcus Tillman',
    detail: '41, Denver CO',
  },
  {
    quote: "I've spent two years bouncing between doctors trying to figure out why I can't lose weight. This thing looked at my fasting insulin, hs-CRP, and cortisol together — in context — and suddenly the pattern was obvious. I cried.",
    name: 'Jennifer Kwon',
    detail: '29, Brooklyn NY',
  },
];

const faqs = [
  { q: 'What happens to my data after I upload?', a: "Your lab results are encrypted end-to-end using AES-256 and stored in HIPAA-compliant infrastructure. They're never shared with insurers, employers, data brokers, or anyone else. You can delete your data permanently at any time from your account settings." },
  { q: 'Does this replace my doctor?', a: "No. Hemologix is an educational tool that helps you understand your lab results in more depth. It doesn't diagnose conditions or prescribe treatments. Think of it as preparation — so you walk into your next appointment with better questions and more context." },
  { q: 'What lab format do I need?', a: "Any format works — PDF, photo, or scanned image. The AI reads results from Quest, Labcorp, hospital labs, direct-to-consumer panels, and most international formats. If it can't read something, it'll tell you." },
  { q: 'How recent do my labs need to be?', a: "The AI will analyze labs of any age, but results from the last 6 months give the most actionable picture. If your labs are older, the report will flag which biomarkers are worth re-testing first." },
  { q: "What are 'functional' and 'optimal' ranges?", a: 'Standard lab ranges tell you if you\'re sick. Functional ranges, used by integrative and functional medicine practitioners, identify where your levels sit relative to where the body performs best. For example, standard TSH range is 0.5–4.5. Many functional practitioners consider 1.0–2.0 optimal.' },
  { q: 'Do I have to buy supplements or see a doctor?', a: "No. The free AI analysis is a complete product on its own — many people take their report straight to their existing doctor. Supplements and physician consultations are available if you want them, but there's zero pressure or upselling in the free report." },
];

import {
  oversizedFileError,
  wrongFormatError,
  type FileUploadError,
} from '../../services/fileUploadErrors';

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per CAP.01 spec

// ─── Component ───

export function LandingPage() {
  const { dispatch } = useApp();
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [hovStep, setHovStep] = useState<number | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [uploadError, setUploadError] = useState<FileUploadError | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [comingSoonEmail, setComingSoonEmail] = useState('');
  const [comingSoonSubmitted, setComingSoonSubmitted] = useState(false);

  // Launch gate removed 2026-04-04 — pipeline certified, flow is live.

  useEffect(() => {
    const h = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  // Track page view on mount
  useEffect(() => {
    trackFunnelEvent({ event: 'PAGE_VIEW', metadata: { page: 'landing' } });
  }, []);

  function startFlow(screen: string = 'triage') {
    dispatch({ type: 'SET_SCREEN', payload: screen as import('../../types').AppScreen });
    navigate('/app');
  }

  function handleFileAccepted(file: File) {
    trackFunnelEvent({ event: 'LAB_UPLOAD', metadata: { fileName: file.name, fileSize: file.size } });
    window.__hemologixUpload = file;
    startFlow('triage');
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setUploadError(wrongFormatError(file.name));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(oversizedFileError(file.size));
      return;
    }
    setUploadError(null);
    handleFileAccepted(file);
  }

  function triggerUpload() {
    setUploadError(null);
    fileInputRef.current?.click();
  }

  return (
    <div style={{ backgroundColor: C.bg, color: C.text, minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif", width: '100%', overflowX: 'hidden' }}>
      {/* Skip link */}
      <a
        href="#main-content"
        style={{
          position: 'absolute', top: -40, left: 0, padding: '8px 16px',
          background: C.btn, color: 'white', fontSize: 14, zIndex: 1100, transition: 'top .2s',
        }}
        onFocus={(e) => { (e.target as HTMLElement).style.top = '0'; }}
        onBlur={(e) => { (e.target as HTMLElement).style.top = '-40px'; }}
      >
        Skip to content
      </a>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <style>{`
        ::selection{background:${C.accent};color:white}
        @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse{0%,100%{opacity:.5}50%{opacity:1}}
        @keyframes shimmer{0%{background-position:-200% center}100%{background-position:200% center}}

        .btn-primary{
          background:${C.btn};color:white;border:none;padding:16px 40px;
          font-family:'DM Sans',sans-serif;font-size:15px;font-weight:600;
          letter-spacing:.2px;border-radius:4px;cursor:pointer;
          transition:all .25s cubic-bezier(.16,1,.3,1);
        }
        .btn-primary:hover{background:${C.btnHover};transform:translateY(-2px);box-shadow:0 8px 24px ${C.btnGlow}}
        .btn-ghost{
          background:transparent;color:${C.text};border:1.5px solid ${C.border};
          padding:14px 36px;font-family:'DM Sans',sans-serif;font-size:15px;
          font-weight:500;border-radius:4px;cursor:pointer;transition:all .25s;
        }
        .btn-ghost:hover{border-color:${C.text};background:${C.borderLight}}
        .btn-rose{
          background:${C.accent};color:white;border:none;padding:16px 40px;
          font-family:'DM Sans',sans-serif;font-size:15px;font-weight:600;
          letter-spacing:.2px;border-radius:4px;cursor:pointer;
          transition:all .25s cubic-bezier(.16,1,.3,1);
        }
        .btn-rose:hover{background:#BE123C;transform:translateY(-2px);box-shadow:0 8px 24px rgba(225,29,72,.18)}
        .label{font-size:12px;font-weight:600;letter-spacing:2.5px;text-transform:uppercase;color:${C.accent};margin-bottom:14px}
        .card{background:${C.bgCard};border:1px solid ${C.border};border-radius:6px;transition:all .3s cubic-bezier(.16,1,.3,1)}
        .card:hover{border-color:${C.textMuted};box-shadow:0 8px 32px rgba(0,0,0,.04);transform:translateY(-3px)}
        .row-hover{transition:background .15s}.row-hover:hover{background:${C.accentSoft}}
        button:focus-visible,a:focus-visible{outline:2px solid ${C.accent};outline-offset:3px}

        .g4{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
        .g3{display:grid;grid-template-columns:repeat(3,1fr);gap:18px}
        .g2-ai{display:grid;grid-template-columns:1fr 1fr;gap:64px;align-items:center}
        .g4-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:32px;text-align:center}
        .g3-team{display:flex;justify-content:center;gap:32px}
        .g4-footer{display:grid;grid-template-columns:2fr 1fr 1fr 1fr;gap:44px}
        .bio-row{display:grid;grid-template-columns:1fr 160px 160px;padding:12px 28px;align-items:center}
        .bio-head{display:grid;grid-template-columns:1fr 160px 160px;padding:16px 28px}
        .hero-flow{display:flex;gap:28px;flex-wrap:wrap;justify-content:center}
        .hero-btns{display:flex;gap:12px;flex-wrap:wrap;justify-content:center}
        .cta-btns{display:flex;gap:12px;justify-content:center;flex-wrap:wrap}
        .nav-inner{padding:0 48px}
        .section-pad{padding:100px 24px}
        .hero-pad{padding:120px 24px 80px}

        @media(max-width:768px){
          .g4{grid-template-columns:repeat(2,1fr);gap:14px}
          .g3{grid-template-columns:1fr;gap:16px}
          .g2-ai{grid-template-columns:1fr;gap:40px}
          .g4-stats{grid-template-columns:repeat(2,1fr);gap:24px}
          .g3-team{flex-wrap:wrap;gap:24px}
          .g4-footer{grid-template-columns:1fr 1fr;gap:32px}
          .bio-row{grid-template-columns:1fr 100px 100px;padding:10px 16px}
          .bio-head{grid-template-columns:1fr 100px 100px;padding:12px 16px}
          .nav-inner{padding:0 20px}
          .section-pad{padding:72px 20px}
          .hero-pad{padding:100px 20px 60px}
        }

        @media(max-width:480px){
          .g4{grid-template-columns:1fr}
          .g3{grid-template-columns:1fr}
          .g4-stats{grid-template-columns:repeat(2,1fr);gap:20px}
          .g3-team{flex-direction:column;align-items:center;gap:20px}
          .g4-footer{grid-template-columns:1fr;gap:28px}
          .bio-row{grid-template-columns:1fr 72px 72px;padding:8px 12px;font-size:12px}
          .bio-head{grid-template-columns:1fr 72px 72px;padding:10px 12px}
          .hero-flow{flex-direction:column;align-items:center;gap:12px}
          .hero-flow .flow-arrow{display:none}
          .hero-btns{flex-direction:column;width:100%}
          .hero-btns .btn-primary,.hero-btns .btn-ghost,.hero-btns .btn-rose{width:100%;text-align:center}
          .cta-btns{flex-direction:column;align-items:center}
          .cta-btns .btn-primary,.cta-btns .btn-ghost,.cta-btns .btn-rose{width:100%;max-width:340px;text-align:center}
          .nav-inner{padding:0 16px}
          .section-pad{padding:56px 16px}
          .hero-pad{padding:88px 16px 48px}
          .btn-primary{padding:14px 28px;font-size:14px}
          .btn-ghost{padding:12px 24px;font-size:14px}
          .btn-rose{padding:14px 28px;font-size:14px}
        }

        @media(prefers-reduced-motion:reduce){*,*::before,*::after{animation-duration:.01ms!important;transition-duration:.01ms!important}}

        .sticky-cta{position:fixed;bottom:0;left:0;right:0;z-index:999;padding:12px 16px;padding-bottom:max(16px,env(safe-area-inset-bottom));background:rgba(255,255,255,.96);backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);border-top:1px solid ${C.border};transform:translateY(100%);transition:transform .35s cubic-bezier(.16,1,.3,1)}
        .sticky-cta.visible{transform:translateY(0)}
        .sticky-spacer{display:none}
        @media(max-width:768px){.sticky-spacer{display:block;height:72px}}
        @media(min-width:769px){.sticky-cta{display:none}}
      `}</style>

      {/* ─── NAV ─── */}
      <nav
        className="nav-inner"
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, height: 64,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          backgroundColor: scrollY > 60 ? C.glass : 'transparent',
          backdropFilter: scrollY > 60 ? 'blur(20px) saturate(180%)' : 'none',
          borderBottom: `1px solid ${scrollY > 60 ? C.border : 'transparent'}`,
          transition: 'all .3s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 3,
            background: `linear-gradient(135deg,${C.accent},#FB7185)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, color: 'white',
          }}>
            H
          </div>
          <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: 1.8, color: C.navy }}>HEMOLOGIX</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => navigate('/doctor')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 500, color: C.textSec,
              fontFamily: "'DM Sans', sans-serif",
              padding: '10px 8px',
              transition: 'color .2s',
            }}
            onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.color = C.accent; }}
            onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.color = C.textSec; }}
          >
            Meet Dr. C
          </button>
          <button
            onClick={() => navigate('/catalog')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 500, color: C.textSec,
              fontFamily: "'DM Sans', sans-serif",
              padding: '10px 8px',
              transition: 'color .2s',
            }}
            onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.color = C.accent; }}
            onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.color = C.textSec; }}
          >
            Supplements
          </button>
          <a
            href="/research"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 500, color: C.textSec,
              fontFamily: "'DM Sans', sans-serif",
              padding: '10px 8px',
              transition: 'color .2s',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => { (e.target as HTMLAnchorElement).style.color = C.accent; }}
            onMouseLeave={(e) => { (e.target as HTMLAnchorElement).style.color = C.textSec; }}
          >
            Research
          </a>
          <button
            onClick={() => navigate('/support')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 500, color: C.textSec,
              fontFamily: "'DM Sans', sans-serif",
              padding: '10px 8px',
              transition: 'color .2s',
            }}
            onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.color = C.accent; }}
            onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.color = C.textSec; }}
          >
            Support
          </button>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 500, color: C.textSec,
              fontFamily: "'DM Sans', sans-serif",
              padding: '10px 8px',
              transition: 'color .2s',
            }}
            onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.color = C.accent; }}
            onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.color = C.textSec; }}
          >
            Sign In
          </button>
          <button className="btn-primary" style={{ padding: '10px 26px', fontSize: 13 }} onClick={triggerUpload}>
            Get Started
          </button>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section
        id="main-content"
        className="hero-pad"
        style={{
          minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', textAlign: 'center', position: 'relative', overflow: 'hidden',
        }}
      >
        <div style={{
          position: 'absolute', inset: 0, opacity: .3,
          backgroundImage: `linear-gradient(${C.border} 1px, transparent 1px), linear-gradient(90deg, ${C.border} 1px, transparent 1px)`,
          backgroundSize: '72px 72px',
          maskImage: 'radial-gradient(ellipse 70% 55% at 50% 40%, black 20%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse 70% 55% at 50% 40%, black 20%, transparent 75%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', width: '60%', height: '50%', top: '20%', left: '20%',
          background: `radial-gradient(ellipse,${C.accentSoft} 0%,transparent 70%)`,
          pointerEvents: 'none', opacity: .6,
        }} />

        <div style={{
          animation: 'fadeUp .8s ease-out', marginBottom: 32,
          padding: '7px 18px', border: `1px solid ${C.border}`, borderRadius: 40,
          fontSize: 12, fontWeight: 600, letterSpacing: 2, color: C.textSec,
          background: C.glass, backdropFilter: 'blur(10px)',
          display: 'flex', alignItems: 'center', gap: 9,
        }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#10B981', animation: 'pulse 2s ease-in-out infinite' }} />
          AI-POWERED BLOODWORK ANALYSIS
        </div>

        <h1 style={{
          fontFamily: "'Instrument Serif',Georgia,serif", fontSize: 'clamp(36px,6.5vw,78px)',
          fontWeight: 400, lineHeight: 1.08, maxWidth: 780, animation: 'fadeUp 1s ease-out .2s both',
          letterSpacing: '-1.5px', color: C.navy,
        }}>
          Your Labs Say <em>Normal.</em>{' '}But You Don't Feel{' '}<span style={{
            background: `linear-gradient(135deg,${C.accent},#FB7185)`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontStyle: 'italic',
          }}>Normal.</span>
        </h1>

        <p style={{
          fontSize: 'clamp(15px,1.8vw,18px)', fontWeight: 400, lineHeight: 1.7,
          color: C.textSec, maxWidth: 460, marginTop: 24, animation: 'fadeUp 1s ease-out .5s both',
        }}>
          Fatigue. Brain fog. Weight changes you can't explain.
          The answers might already be sitting in your lab results.
        </p>
        <p style={{
          fontSize: 'clamp(14px,1.6vw,17px)', fontWeight: 400, lineHeight: 1.7,
          color: C.textMuted, maxWidth: 420, marginTop: 8, animation: 'fadeUp 1s ease-out .55s both',
        }}>
          Upload any bloodwork — Quest, Labcorp, any provider — and
          the AI shows you what a 90-second review couldn't.
        </p>

        <div className="hero-btns" style={{ marginTop: 36, animation: 'fadeUp 1s ease-out .65s both' }}>
          <button
            className="btn-rose"
            style={{ padding: '18px 44px', fontSize: 16 }}
            onClick={() => {
              trackFunnelEvent({ event: 'CTA_CLICK', metadata: { source: 'hero', label: 'talk_to_nurse' } });
              startFlow('triage');
            }}
          >
            Talk to Our AI Nurse — Free
          </button>
          <button className="btn-ghost" onClick={triggerUpload}>
            Upload Your Labs Instead
          </button>
        </div>

        {uploadError && (
          <div style={{ marginTop: 12, color: C.accent, fontSize: 13, fontWeight: 500, animation: 'fadeUp .4s ease-out' }}>
            <strong>{uploadError.title}.</strong> {uploadError.message}
          </div>
        )}

        <div className="hero-flow" style={{ marginTop: 40, animation: 'fadeUp 1s ease-out .8s both', fontSize: 13, color: C.textSec }}>
          {[
            { icon: '↑', text: 'Upload any lab' },
            { icon: '◎', text: 'AI analyzes in under 2 min' },
            { icon: '↓', text: 'Get a private, downloadable report' },
          ].map((s, i) => (
            <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                width: 24, height: 24, borderRadius: 3, border: `1px solid ${C.border}`,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, color: C.textMuted,
              }}>{s.icon}</span>
              {s.text}
              {i < 2 && <span className="flow-arrow" style={{ color: C.border, marginLeft: 16 }}>→</span>}
            </span>
          ))}
        </div>

        <div style={{
          marginTop: 20, animation: 'fadeUp 1s ease-out .9s both',
          fontSize: 12, color: C.textMuted, letterSpacing: .3,
          display: 'flex', alignItems: 'center', gap: 6, textAlign: 'center', flexWrap: 'wrap', justifyContent: 'center',
        }}>
          <span style={{ fontSize: 14 }}>&#x1F512;</span>
          End-to-end encrypted · HIPAA-compliant · Never shared with insurers or employers · Delete anytime
        </div>
      </section>

      {/* ─── STATS ─── */}
      <section style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, padding: '44px 24px', background: C.bgSoft }}>
        <div className="g4-stats" style={{ maxWidth: 1060, margin: '0 auto' }}>
          {[
            { value: '40+', label: 'Biomarkers Analyzed Per Panel' },
            { value: '3', label: 'Range Types Compared', sub: 'Standard · Functional · Optimal' },
            { value: '<2 min', label: 'From Upload to Full Report' },
            { value: '87.7%', label: 'AI Medical Exam Accuracy' },
          ].map((s, i) => (
            <FadeIn key={i} delay={i * .08}>
              <div>
                <div style={{ fontFamily: "'Instrument Serif',serif", fontSize: 'clamp(30px,4vw,38px)', fontWeight: 400, color: C.navy, lineHeight: 1, marginBottom: 4 }}>{s.value}</div>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 2, color: C.textMuted, textTransform: 'uppercase' }}>{s.label}</div>
                {s.sub && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4, fontStyle: 'italic' }}>{s.sub}</div>}
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ─── AI CHAT PREVIEW ─── */}
      <section className="section-pad" style={{ maxWidth: 1060, margin: '0 auto' }}>
        <div className="g2-ai">
          <div>
            <FadeIn><div className="label">YOUR AI HEALTH ASSISTANT</div></FadeIn>
            <FadeIn delay={.1}>
              <h2 style={{ fontFamily: "'Instrument Serif',serif", fontSize: 'clamp(24px,3.5vw,42px)', fontWeight: 400, lineHeight: 1.15, marginBottom: 18 }}>
                The lab analysis your doctor would do{' '}<span style={{ color: C.accent, fontStyle: 'italic' }}>if they had an hour.</span>
              </h2>
            </FadeIn>
            <FadeIn delay={.2}>
              <p style={{ fontSize: 15, lineHeight: 1.8, color: C.textSec, marginBottom: 28 }}>
                This AI doesn't diagnose or prescribe — it's a comparison tool. It reads your
                labs, plots every biomarker against published functional and optimal ranges,
                and explains what the research says. So you walk into your next appointment
                with better questions.
              </p>
            </FadeIn>
            <FadeIn delay={.3}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {[
                  { label: 'Universal Lab Reader', desc: 'Reads 40+ biomarkers from any lab format — Quest, Labcorp, hospital, international' },
                  { label: 'Three-Range Comparison', desc: 'Shows where you fall across standard, functional, and optimal ranges side by side' },
                  { label: 'Plain-Language Walkthrough', desc: 'Every result explained like a knowledgeable, patient friend' },
                  { label: 'Cited Research', desc: 'References linked to the studies behind each insight — not black-box conclusions' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '14px 0', borderBottom: `1px solid ${C.borderLight}` }}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: C.accent, marginTop: 7, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 1 }}>{item.label}</div>
                      <div style={{ fontSize: 14, color: C.textSec }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={.2} direction="left">
            <div style={{
              border: `1px solid ${C.border}`, borderRadius: 7, background: C.bgCard,
              padding: 'clamp(16px,3vw,24px)', position: 'relative', overflow: 'hidden',
              boxShadow: `0 1px 3px rgba(0,0,0,.04), 0 12px 40px rgba(0,0,0,.04)`,
            }}>
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 3, borderRadius: '7px 7px 0 0',
                background: `linear-gradient(90deg,${C.accent},#FB7185,${C.accent})`,
                backgroundSize: '200% 100%', animation: 'shimmer 4s linear infinite',
              }} />
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: C.textMuted, marginBottom: 18 }}>AI HEALTH ASSISTANT — SAMPLE</div>
              {[
                { role: 'ai', text: "Looking at your thyroid panel — TSH is 3.4. That's within the standard reference range, but functional practitioners generally consider 1.0–2.0 optimal. Here's why that gap matters..." },
                { role: 'ai', text: 'Your Free T3 sits near the bottom of reference range, and TPO antibodies are at 48. Research suggests these patterns are worth flagging for your provider — especially together.', hl: true },
                { role: 'user', text: 'Nobody has ever explained it this way. What should I do?' },
                { role: 'ai', text: "That's a great question for your doctor — and now you have the context to ask it well. Here's a summary of your key findings to bring to your next visit, with the research references behind each one." },
              ].map((m, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
                  <div style={{
                    maxWidth: '85%', padding: '12px 15px',
                    borderRadius: m.role === 'user' ? '5px 5px 1px 5px' : '5px 5px 5px 1px',
                    background: m.role === 'user' ? C.btn : ('hl' in m && m.hl) ? C.accentSoft : C.bgSoft,
                    border: ('hl' in m && m.hl) ? '1px solid rgba(225,29,72,.1)' : 'none',
                    fontSize: 13, lineHeight: 1.6, color: m.role === 'user' ? 'white' : C.text,
                  }}>{m.text}</div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 4, padding: '4px 0' }}>
                {[0, 1, 2].map(i => <div key={i} style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: C.accent, opacity: .3, animation: `pulse 1.2s ease-in-out ${i * .2}s infinite` }} />)}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── THE GAP — biomarker table ─── */}
      <section className="section-pad" style={{ background: C.bgSoft, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 1060, margin: '0 auto' }}>
          <FadeIn><div className="label">THE GAP</div></FadeIn>
          <FadeIn delay={.1}>
            <h2 style={{ fontFamily: "'Instrument Serif',serif", fontSize: 'clamp(26px,4vw,48px)', fontWeight: 400, lineHeight: 1.12, maxWidth: 700, marginBottom: 20 }}>
              Your labs hold more{' '}<span style={{ color: C.accent, fontStyle: 'italic' }}>answers</span>{' '}than the pass/fail system reveals.
            </h2>
          </FadeIn>
          <FadeIn delay={.2}>
            <p style={{ fontSize: 16, lineHeight: 1.8, color: C.textSec, maxWidth: 560, marginBottom: 44 }}>
              A standard office visit gives your doctor about 90 seconds with your bloodwork.
              Enough to check if anything is flagged — not enough to compare your results
              against the ranges where your body actually performs best.
            </p>
          </FadeIn>

          <FadeIn delay={.3}>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, overflow: 'hidden', background: C.bgCard, minWidth: 480 }}>
                <div className="bio-head" style={{ borderBottom: `1px solid ${C.border}`, background: C.bgSoft }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: C.textMuted, textTransform: 'uppercase' }}>Biomarker</span>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: C.textMuted, textTransform: 'uppercase', textAlign: 'center' }}>Standard</span>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: C.accent, textTransform: 'uppercase', textAlign: 'center' }}>Hemologix</span>
                </div>
                {biomarkers.map((m, i) => (
                  <div key={i} className="bio-row row-hover" style={{ borderBottom: i < biomarkers.length - 1 ? `1px solid ${C.borderLight}` : 'none' }}>
                    <span style={{ fontSize: 14, color: m.std ? C.textSec : C.text, fontWeight: m.std ? 400 : 500 }}>{m.name}</span>
                    <span style={{ textAlign: 'center' }}>{m.std ? <span style={{ color: C.textMuted, fontSize: 12, fontStyle: 'italic' }}>Glanced</span> : <span style={{ color: C.textMuted }}>—</span>}</span>
                    <span style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: C.accent }}>Deep Analysis</span>
                  </div>
                ))}
                <div className="bio-row" style={{ borderTop: `1px solid ${C.border}`, background: C.accentSoft }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.textSec }}>Depth</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, textAlign: 'center' }}>In/Out</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.accent, textAlign: 'center' }}>Functional · Optimal · Elite</span>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="section-pad" style={{ background: C.bg }}>
        <div style={{ maxWidth: 1060, margin: '0 auto' }}>
          <FadeIn><div className="label" style={{ textAlign: 'center' }}>HOW IT WORKS</div></FadeIn>
          <FadeIn delay={.1}>
            <h2 style={{ fontFamily: "'Instrument Serif',serif", fontSize: 'clamp(26px,4vw,46px)', fontWeight: 400, textAlign: 'center', marginBottom: 52, lineHeight: 1.15 }}>
              From uploaded labs to{' '}<span style={{ fontStyle: 'italic', color: C.accent }}>clarity</span>{' '}in minutes.
            </h2>
          </FadeIn>
          <div className="g4">
            {steps.map((s, i) => (
              <FadeIn key={i} delay={i * .08} style={{ height: '100%' }}>
                <div
                  className="card"
                  style={{ padding: '32px 24px', height: '100%', cursor: 'default' }}
                  onMouseEnter={() => setHovStep(i)}
                  onMouseLeave={() => setHovStep(null)}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: 4,
                    background: hovStep === i ? C.btn : C.borderLight,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, color: hovStep === i ? 'white' : C.textSec,
                    transition: 'all .25s', marginBottom: 18,
                  }}>{s.icon}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2.5, color: C.accent, marginBottom: 8, textTransform: 'uppercase' }}>Step {s.n}</div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, lineHeight: 1.2 }}>{s.title}</h3>
                  <p style={{ fontSize: 14, lineHeight: 1.7, color: C.textSec }}>{s.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── MEDGEMMA ─── */}
      <section style={{ padding: 'clamp(56px,8vw,80px) 24px', background: C.bgDark, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', width: '40%', height: '100%', top: 0, right: 0, background: 'radial-gradient(ellipse at right,rgba(225,29,72,.06) 0%,transparent 70%)', pointerEvents: 'none' }} />
        <div style={{
          position: 'absolute', inset: 0, opacity: .06,
          backgroundImage: 'linear-gradient(rgba(255,255,255,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.15) 1px, transparent 1px)',
          backgroundSize: '48px 48px', pointerEvents: 'none',
        }} />
        <div style={{ maxWidth: 1060, margin: '0 auto', position: 'relative' }}>
          <FadeIn>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
              <div style={{ padding: '5px 16px', borderRadius: 40, background: 'rgba(225,29,72,.1)', border: '1px solid rgba(225,29,72,.15)', fontSize: 11, fontWeight: 700, letterSpacing: 2.5, color: '#FB7185', textTransform: 'uppercase' }}>THE ENGINE</div>
            </div>
          </FadeIn>
          <FadeIn delay={.1}>
            <h2 style={{ fontFamily: "'Instrument Serif',serif", fontSize: 'clamp(24px,3.5vw,42px)', fontWeight: 400, textAlign: 'center', marginBottom: 10, lineHeight: 1.15, color: '#E4E4E7' }}>
              Powered by{' '}<span style={{ background: 'linear-gradient(135deg,#FB7185,#FBBF24)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontStyle: 'italic' }}>Google's MedGemma</span>
            </h2>
          </FadeIn>
          <FadeIn delay={.15}>
            <p style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(161,161,170,.6)', textAlign: 'center', maxWidth: 520, margin: '0 auto 44px' }}>
              Not a general chatbot guessing at health. MedGemma is Google DeepMind's
              medical-specialist AI — trained exclusively on clinical research and
              built from the ground up to understand lab results.
            </p>
          </FadeIn>
          <FadeIn delay={.2}>
            <div className="g4">
              {[
                { headline: 'More research than any doctor could read in 10 lifetimes', footnote: '27 billion parameters trained on peer-reviewed medical literature, clinical guidelines, and health records' },
                { headline: 'Scores within 3 points of licensed physicians on medical board exams', footnote: "87.7% on MedQA — the standard benchmark for medical licensing exam questions" },
                { headline: 'The most accurate medical AI publicly available', footnote: '#1 ranked open medical model — outperforms all base models on every benchmark tested' },
                { headline: "Built by Google's medical AI team", footnote: "Part of Google DeepMind's Health AI Developer Foundations — open, auditable, peer-reviewed" },
              ].map((item, i) => (
                <div key={i} style={{ padding: '26px 20px', background: C.bgDarkCard, border: `1px solid ${C.bgDarkBorder}`, borderRadius: 5 }}>
                  <div style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.5, color: '#E4E4E7', marginBottom: 12 }}>{item.headline}</div>
                  <div style={{ fontSize: 11, lineHeight: 1.5, color: 'rgba(161,161,170,.3)', borderTop: `1px solid ${C.bgDarkBorder}`, paddingTop: 10 }}>{item.footnote}</div>
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={.3}>
            <div style={{ textAlign: 'center', marginTop: 32, fontSize: 12, color: 'rgba(161,161,170,.25)', fontStyle: 'italic', maxWidth: 600, margin: '32px auto 0' }}>
              MedGemma is developed by Google under the Health AI Developer Foundations. Hemologix uses MedGemma as an analytical engine — all clinical decisions are made by licensed physicians.
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── WHO BUILT THIS ─── */}
      <section className="section-pad" style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <FadeIn><div className="label">WHO BUILT THIS</div></FadeIn>
          <FadeIn delay={.1}>
            <h2 style={{ fontFamily: "'Instrument Serif',serif", fontSize: 'clamp(20px,3vw,34px)', fontWeight: 400, lineHeight: 1.3, marginBottom: 28, color: C.navy }}>
              <span style={{ fontStyle: 'italic' }}>"I built this because my wife's doctor missed her Hashimoto's for three years. Her labs had the answer the whole time — nobody had 20 minutes to actually look."</span>
            </h2>
          </FadeIn>
          <FadeIn delay={.2}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 48, height: 48, borderRadius: 4, background: C.borderLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: C.textMuted }}>&#x1F464;</div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>[Founder Name]</div>
                <div style={{ fontSize: 13, color: C.textMuted }}>CEO &amp; Co-Founder, Hemologix</div>
              </div>
            </div>
          </FadeIn>
          <FadeIn delay={.3}>
            <div className="g3-team" style={{ marginTop: 36, paddingTop: 28, borderTop: `1px solid ${C.border}` }}>
              {[
                { name: 'Dr. Christopher Calapai, D.O.', role: 'Medical Director', cred: 'Functional Medicine, 40+ years', link: '/doctor' },
                { name: '[Dr. Name]', role: 'Medical Advisor', cred: 'Board-Certified Endocrinologist', link: null },
                { name: '[Name]', role: 'AI Lead', cred: 'Formerly Google Health AI', link: null },
              ].map((p, i) => (
                <div
                  key={i}
                  style={{ textAlign: 'center', cursor: p.link ? 'pointer' : 'default', transition: 'transform 0.15s' }}
                  onClick={() => p.link && navigate(p.link)}
                  onMouseEnter={(e) => { if (p.link) e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { if (p.link) e.currentTarget.style.transform = 'none'; }}
                >
                  <div style={{ width: 40, height: 40, borderRadius: 4, background: p.link ? C.accentSoft : C.borderLight, border: p.link ? `1px solid rgba(225,29,72,0.15)` : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: p.link ? C.accent : C.textMuted, margin: '0 auto 8px' }}>{p.link ? '\u2695' : '\u{1F464}'}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: C.accent, fontWeight: 500 }}>{p.role}</div>
                  <div style={{ fontSize: 11, color: C.textMuted }}>{p.cred}</div>
                  {p.link && <div style={{ fontSize: 11, color: C.accent, fontWeight: 500, marginTop: 4 }}>Meet the Doctor &rsaquo;</div>}
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="section-pad" style={{ background: C.bgSoft }}>
        <div style={{ maxWidth: 1060, margin: '0 auto' }}>
          <FadeIn><div className="label" style={{ textAlign: 'center' }}>REAL RESULTS</div></FadeIn>
          <FadeIn delay={.1}>
            <h2 style={{ fontFamily: "'Instrument Serif',serif", fontSize: 'clamp(24px,3.5vw,42px)', fontWeight: 400, textAlign: 'center', marginBottom: 52, lineHeight: 1.15 }}>
              The moment your labs finally{' '}<span style={{ fontStyle: 'italic', color: C.accent }}>make sense.</span>
            </h2>
          </FadeIn>
          <div className="g3">
            {testimonials.map((t, i) => (
              <FadeIn key={i} delay={i * .08} style={{ height: '100%' }}>
                <div className="card" style={{ padding: 'clamp(20px,3vw,32px)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: 28, color: C.accent, marginBottom: 10, lineHeight: 1, fontFamily: "'Instrument Serif',serif" }}>&ldquo;</div>
                  <p style={{ fontSize: 14, lineHeight: 1.7, color: C.textSec, marginBottom: 18, flex: 1 }}>{t.quote}</p>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{t.name}</div>
                    <div style={{ fontSize: 13, color: C.textMuted }}>{t.detail}</div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── YOUR CALL ─── */}
      <section className="section-pad" style={{ borderTop: `1px solid ${C.border}`, background: C.bg }}>
        <div style={{ maxWidth: 1060, margin: '0 auto' }}>
          <FadeIn><div className="label" style={{ textAlign: 'center' }}>YOUR CALL</div></FadeIn>
          <FadeIn delay={.1}>
            <h2 style={{ fontFamily: "'Instrument Serif',serif", fontSize: 'clamp(24px,3.5vw,42px)', fontWeight: 400, marginBottom: 14, lineHeight: 1.15, textAlign: 'center' }}>
              Go as deep as you want.{' '}<span style={{ color: C.accent, fontStyle: 'italic' }}>Or don't.</span>
            </h2>
          </FadeIn>
          <FadeIn delay={.2}>
            <p style={{ fontSize: 15, lineHeight: 1.8, color: C.textSec, maxWidth: 500, margin: '0 auto 52px', textAlign: 'center' }}>
              Some people take the free report straight to their doctor.
              Some want supplement guidance. Some want a full clinical plan.
              Every option exists when you're ready — nothing is required.
            </p>
          </FadeIn>
          <FadeIn delay={.3}>
            <div className="g4">
              {[
                { icon: '◎', phase: 'FREE', title: 'AI Analysis', desc: "Upload any lab. See where every biomarker falls across standard, functional, and optimal ranges — explained in plain language.", active: true },
                { icon: '⚡', phase: 'OPTIONAL', title: 'Targeted Supplements', desc: 'Evidence-based options matched to the gaps in your report. Buy only what the data supports — or take the list to your own provider.' },
                { icon: '⚕', phase: 'OPTIONAL', title: 'Provider Review', desc: 'Dr. Calapai — 40+ years in functional medicine — reviews your full case and builds a personalized clinical plan around your results.', link: '/doctor' },
                { icon: '∞', phase: 'OPTIONAL', title: 'Ongoing Care', desc: 'Regular labs, adjusted protocols, and a provider who tracks your numbers as they change over time.' },
              ].map((item: { icon: string; phase: string; title: string; desc: string; active?: boolean; link?: string }, i) => (
                <div
                  key={i}
                  className="card"
                  style={{
                    padding: '28px 20px', textAlign: 'center', position: 'relative',
                    borderColor: item.active ? C.accent : C.border,
                    cursor: item.link ? 'pointer' : 'default',
                    transition: 'box-shadow 0.2s, transform 0.15s',
                  }}
                  onClick={() => item.link && navigate(item.link)}
                  onMouseEnter={(e) => { if (item.link) { e.currentTarget.style.boxShadow = '0 4px 16px rgba(225,29,72,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                  onMouseLeave={(e) => { if (item.link) { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; } }}
                >
                  <div style={{
                    position: 'absolute', top: -1, left: '50%', transform: 'translateX(-50%)',
                    padding: '3px 14px', borderRadius: 40,
                    background: item.active ? C.btn : C.bgCard,
                    border: item.active ? 'none' : `1px solid ${C.border}`,
                    fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
                    color: item.active ? 'white' : C.textMuted, whiteSpace: 'nowrap',
                  }}>{item.phase}</div>
                  <div style={{ fontSize: 26, marginTop: 14, marginBottom: 12, opacity: .45 }}>{item.icon}</div>
                  <h3 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8, lineHeight: 1.2 }}>{item.title}</h3>
                  <p style={{ fontSize: 13, lineHeight: 1.7, color: C.textSec }}>{item.desc}</p>
                  {item.link && <div style={{ fontSize: 12, color: C.accent, fontWeight: 600, marginTop: 10 }}>Meet Dr. Calapai &rsaquo;</div>}
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="section-pad" style={{ background: C.bgSoft, borderTop: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <FadeIn><div className="label" style={{ textAlign: 'center' }}>COMMON QUESTIONS</div></FadeIn>
          <FadeIn delay={.1}>
            <h2 style={{ fontFamily: "'Instrument Serif',serif", fontSize: 'clamp(24px,3.5vw,42px)', fontWeight: 400, textAlign: 'center', marginBottom: 44, lineHeight: 1.15 }}>
              Anything else?
            </h2>
          </FadeIn>
          {faqs.map((f, i) => (
            <FadeIn key={i} delay={i * .05}>
              <div style={{ borderBottom: `1px solid ${C.border}` }}>
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenFaq(openFaq === i ? null : i); } }}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 0', gap: 16, cursor: 'pointer' }}
                >
                  <span style={{ fontSize: 15, fontWeight: 500, color: C.text }}>{f.q}</span>
                  <span style={{ fontSize: 20, color: C.textMuted, flexShrink: 0, transition: 'transform .25s', transform: openFaq === i ? 'rotate(45deg)' : 'none' }} aria-hidden="true">+</span>
                </div>
                <div style={{ maxHeight: openFaq === i ? 500 : 0, overflow: 'hidden', transition: 'max-height .35s cubic-bezier(.16,1,.3,1)' }}>
                  <p style={{ fontSize: 14, lineHeight: 1.8, color: C.textSec, paddingBottom: 20 }}>{f.a}</p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ─── FINAL CTA + EMAIL CAPTURE ─── */}
      <section className="section-pad" style={{ textAlign: 'center', position: 'relative', overflow: 'hidden', background: C.bg }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: .25,
          backgroundImage: `linear-gradient(${C.border} 1px, transparent 1px), linear-gradient(90deg, ${C.border} 1px, transparent 1px)`,
          backgroundSize: '72px 72px',
          maskImage: 'radial-gradient(ellipse 55% 50% at 50% 50%, black 20%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse 55% 50% at 50% 50%, black 20%, transparent 75%)',
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'absolute', width: '50%', height: '60%', top: '20%', left: '25%', background: `radial-gradient(ellipse,${C.accentSoft} 0%,transparent 70%)`, pointerEvents: 'none', opacity: .5 }} />

        <FadeIn>
          <h2 style={{ fontFamily: "'Instrument Serif',serif", fontSize: 'clamp(30px,5vw,60px)', fontWeight: 400, lineHeight: 1.08, marginBottom: 18, position: 'relative', color: C.navy }}>
            Stop wondering.<br />
            <span style={{ background: `linear-gradient(135deg,${C.accent},#FB7185)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Start understanding.</span>
          </h2>
        </FadeIn>
        <FadeIn delay={.15}>
          <p style={{ fontSize: 16, color: C.textSec, maxWidth: 420, margin: '0 auto 36px', lineHeight: 1.7, position: 'relative' }}>
            Your labs already have the clues. Upload them now —
            the AI shows you what those numbers really mean.
          </p>
        </FadeIn>
        <FadeIn delay={.25}>
          <div className="cta-btns" style={{ position: 'relative' }}>
            <button
              className="btn-rose"
              style={{ padding: '20px 48px', fontSize: 16 }}
              onClick={() => {
                trackFunnelEvent({ event: 'CTA_CLICK', metadata: { source: 'bottom_cta', label: 'talk_to_nurse' } });
                startFlow('triage');
              }}
            >
              Talk to Our AI Nurse — Free
            </button>
            <button className="btn-ghost" style={{ padding: '20px 36px', fontSize: 16 }} onClick={triggerUpload}>Upload Your Labs Instead</button>
          </div>
        </FadeIn>
        <FadeIn delay={.35}>
          <p style={{ fontSize: 12, color: C.textMuted, marginTop: 18, position: 'relative' }}>No account required · Results in under 2 minutes · HIPAA-grade encryption</p>
        </FadeIn>

        <FadeIn delay={.45}>
          <div style={{ maxWidth: 480, margin: '52px auto 0', padding: 'clamp(20px,3vw,28px)', border: `1px solid ${C.border}`, borderRadius: 6, background: C.bgCard, position: 'relative' }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>Not ready to upload?</p>
            <p style={{ fontSize: 13, color: C.textSec, marginBottom: 16 }}>Get the free guide: <span style={{ fontWeight: 500, fontStyle: 'italic' }}>The 14 Biomarkers Your Doctor Probably Isn't Testing (But Should Be).</span></p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                aria-label="Email address for free biomarker guide"
                style={{
                  flex: '1 1 200px', padding: '12px 14px', border: `1px solid ${C.border}`, borderRadius: 4,
                  fontSize: 14, fontFamily: "'DM Sans',sans-serif", outline: 'none', background: C.bgSoft,
                }}
              />
              <button className="btn-primary" style={{ padding: '12px 24px', fontSize: 13, whiteSpace: 'nowrap' }}>Send the Guide</button>
            </div>
            <p style={{ fontSize: 11, color: C.textMuted, marginTop: 8 }}>No spam. Unsubscribe anytime.</p>
          </div>
        </FadeIn>
      </section>

      {/* ─── FOOTER ─── */}
      <footer style={{ padding: '52px 24px 32px', borderTop: `1px solid ${C.border}`, background: C.bgSoft }}>
        <div className="g4-footer" style={{ maxWidth: 1060, margin: '0 auto' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: 1.8, color: C.navy, marginBottom: 10 }}>HEMOLOGIX</div>
            <p style={{ fontSize: 14, lineHeight: 1.7, color: C.textMuted, maxWidth: 240 }}>AI-powered bloodwork analysis. The context behind every result.</p>
          </div>
          {[
            { title: 'Product', links: [
              { label: 'How It Works', href: '/#how-it-works' },
              { label: 'Supplements', href: '/catalog' },
              { label: 'Get Started', href: '/app' },
              { label: 'FAQ', href: '/#faq' },
            ]},
            { title: 'Company', links: [
              { label: 'Meet Dr. Calapai', href: '/doctor' },
              { label: 'Support', href: '/support' },
              { label: 'Sign In', href: '/login' },
            ]},
            { title: 'Legal', links: [
              { label: 'Privacy Policy', href: '/privacy' },
              { label: 'Terms of Service', href: '/terms' },
              { label: 'Refund Policy', href: '/refund-policy' },
            ]},
          ].map((col, i) => (
            <div key={i}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: C.textMuted, marginBottom: 14, textTransform: 'uppercase' }}>{col.title}</div>
              {col.links.map((l, j) => <div key={j} style={{ fontSize: 14, color: C.textSec, marginBottom: 9, cursor: 'pointer' }} onClick={() => navigate(l.href)}>{l.label}</div>)}
            </div>
          ))}
        </div>
        <div style={{ maxWidth: 1060, margin: '36px auto 0', paddingTop: 18, borderTop: `1px solid ${C.border}` }}>
          <p style={{ fontSize: 12, color: C.textMuted, marginBottom: 8 }}>
            Hemologix is an educational comparison tool that plots lab values against published reference ranges. It does not provide medical diagnoses, treatment recommendations, or clinical advice. All clinical decisions are made by independently licensed physicians.
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <span style={{ fontSize: 12, color: C.textMuted }}>&copy; 2026 Hemologix Health, Inc. All rights reserved.</span>
            <span style={{ fontSize: 10, color: 'rgba(161,161,170,.5)', letterSpacing: .3 }}>These statements have not been evaluated by the FDA.</span>
          </div>
        </div>
      </footer>

      {/* ─── STICKY MOBILE CTA ─── */}
      <div className="sticky-spacer" />
      <div className={`sticky-cta${scrollY > 500 ? ' visible' : ''}`}>
        <button
          onClick={() => {
            trackFunnelEvent({ event: 'CTA_CLICK', metadata: { source: 'sticky_mobile', label: 'start_analysis' } });
            startFlow('triage');
          }}
          style={{
            width: '100%',
            padding: '16px 24px',
            background: C.accent,
            color: 'white',
            border: 'none',
            borderRadius: 4,
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            letterSpacing: 0.3,
          }}
        >
          Start Free Analysis →
        </button>
      </div>

      {/* ─── COMING SOON OVERLAY ─── */}
      {showComingSoon && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => setShowComingSoon(false)}
        >
          <div
            style={{
              background: C.bg,
              borderRadius: 16,
              padding: '48px 36px',
              maxWidth: 440,
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
              position: 'relative',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Brand mark */}
            <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 2, color: C.accent, textTransform: 'uppercase', marginBottom: 8 }}>
              HemoLogix
            </div>

            <h2 style={{ fontSize: 32, fontWeight: 700, color: C.text, margin: '0 0 12px', lineHeight: 1.2 }}>
              Coming Soon
            </h2>

            <p style={{ fontSize: 15, color: C.textSec, lineHeight: 1.6, margin: '0 0 28px' }}>
              We're putting the finishing touches on your AI health navigator.
              Leave your email and we'll notify you when we launch.
            </p>

            {!comingSoonSubmitted ? (
              <form
                onSubmit={e => {
                  e.preventDefault();
                  if (comingSoonEmail.trim()) {
                    const existing = JSON.parse(localStorage.getItem('hemologix_waitlist') || '[]');
                    existing.push({ email: comingSoonEmail.trim(), ts: Date.now() });
                    localStorage.setItem('hemologix_waitlist', JSON.stringify(existing));
                    setComingSoonSubmitted(true);
                  }
                }}
                style={{ display: 'flex', gap: 8, marginBottom: 20 }}
              >
                <input
                  type="email"
                  required
                  placeholder="you@email.com"
                  value={comingSoonEmail}
                  onChange={e => setComingSoonEmail(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '12px 14px',
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    fontSize: 14,
                    fontFamily: "'DM Sans', sans-serif",
                    outline: 'none',
                  }}
                />
                <button
                  type="submit"
                  style={{
                    padding: '12px 22px',
                    background: C.accent,
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: "'DM Sans', sans-serif",
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Notify Me
                </button>
              </form>
            ) : (
              <div style={{ padding: '12px 0 20px', color: C.accent, fontWeight: 600, fontSize: 15 }}>
                You're on the list. We'll be in touch.
              </div>
            )}

            <button
              onClick={() => setShowComingSoon(false)}
              style={{
                background: 'none',
                border: 'none',
                color: C.textMuted,
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              ← Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Extend window for file transfer between screens
declare global {
  interface Window {
    __hemologixUpload?: File;
  }
}
